import { apiRequest } from './core/client';
import { EmailSchema, parseWithSchema } from './schemas';
import type { Email } from '@/types/email';

export type { Email };

// ============================================================================
// Draft Payloads
// ============================================================================

export interface DraftPayloadBase {
  accountId: string;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  html?: string;
  text?: string;
  threadId?: string | null;
  inReplyTo?: string | null;
  references?: string | null;
  attachments?: File[];
}

export type CreateDraftPayload = DraftPayloadBase;

export interface UpdateDraftPayload extends DraftPayloadBase {
  retainedAttachmentIds?: string[];
}

// ============================================================================
// Helpers
// ============================================================================

function buildDraftFormData(
  payload: Omit<DraftPayloadBase, 'accountId'>,
  retainedIds?: string[]
): FormData {
  const { attachments, ...rest } = payload;
  const formData = new FormData();
  (Object.entries(rest) as Array<[string, string | undefined | null]>).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    formData.append(key, value);
  });
  if (Array.isArray(attachments)) {
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });
  }
  if (Array.isArray(retainedIds)) {
    formData.append('retainedAttachmentIds', JSON.stringify(retainedIds));
  }
  return formData;
}

function buildDraftBody(
  payload: Omit<DraftPayloadBase, 'accountId'>,
  retainedIds?: string[]
): Record<string, string | string[] | null> {
  const body: Record<string, string | string[] | null> = {};
  (Object.entries(payload) as Array<[string, string | undefined | null]>).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (key === 'attachments') {
      return;
    }
    body[key] = value;
  });
  if (Array.isArray(retainedIds)) {
    body.retainedAttachmentIds = retainedIds;
  }
  return body;
}

// ============================================================================
// Draft Operations
// ============================================================================

export async function createDraft(payload: CreateDraftPayload): Promise<Email> {
  const { accountId, attachments, ...rest } = payload;
  const requestPayload = { ...rest, attachments };
  const hasFiles = Array.isArray(attachments) && attachments.length > 0;
  const response = await apiRequest<{ draft: unknown }>({
    path: '/emails/drafts',
    method: 'POST',
    accountId,
    ...(hasFiles
      ? { formData: buildDraftFormData(requestPayload) }
      : { body: buildDraftBody(requestPayload) }),
  });
  return parseWithSchema(EmailSchema, response.draft, 'createDraft') as unknown as Email;
}

export async function updateDraft(draftId: string, payload: UpdateDraftPayload): Promise<Email> {
  const { accountId, attachments, retainedAttachmentIds, ...rest } = payload;
  const requestPayload = { ...rest, attachments };
  const hasFiles = Array.isArray(attachments) && attachments.length > 0;
  const response = await apiRequest<{ draft: unknown }>({
    path: `/emails/drafts/${encodeURIComponent(draftId)}`,
    method: 'PUT',
    accountId,
    ...(hasFiles
      ? { formData: buildDraftFormData(requestPayload, retainedAttachmentIds) }
      : { body: buildDraftBody(requestPayload, retainedAttachmentIds) }),
  });
  return parseWithSchema(EmailSchema, response.draft, 'updateDraft') as unknown as Email;
}

export async function deleteDraft(accountId: string, draftId: string): Promise<void> {
  await apiRequest({
    path: `/emails/drafts/${encodeURIComponent(draftId)}`,
    method: 'DELETE',
    accountId,
  });
}

export async function sendDraft(
  accountId: string,
  draftId: string
): Promise<{ messageId: string | null; threadId: string | null }> {
  const response = await apiRequest<{ success: boolean; messageId?: string | null; threadId?: string | null }>({
    path: `/emails/drafts/${encodeURIComponent(draftId)}/send`,
    method: 'POST',
    accountId,
  });
  return {
    messageId: response.messageId ?? null,
    threadId: response.threadId ?? null,
  };
}
